import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CustomDatePicker from '../CustomDatePicker';
import CustomDropdown from '../CustomDropdown';

const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MONTH_OPTS = THAI_MONTHS.map((m, i) => ({ value: i + 1, label: m }));
const DAY_OPTS = Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  recurrence: 'once', scope: 'venue', reason: '',
  startDate: '', endDate: '',
  startMonth: 1, startDay: 1, endMonth: 1, endDay: 1, multiDay: false,
};

const ClosureManagement = () => {
  const { adminUser } = useAdmin();
  const token = adminUser?.idToken;
  const [closures, setClosures] = useState([]);
  const [courtTypes, setCourtTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [affected, setAffected] = useState([]);
  const [checkingAffected, setCheckingAffected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cl, ct] = await Promise.all([
        adminApiService.getClosures(token),
        adminApiService.getCourtTypes(token),
      ]);
      setClosures(cl);
      setCourtTypes(ct);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  const scopeLabel = (scope) => {
    if (scope === 'venue') return 'ทั้งสนาม';
    const ct = courtTypes.find((c) => c.id === scope);
    return ct ? ct.name : scope;
  };
  const scopeOptions = [
    { value: 'venue', label: 'ทั้งสนาม (ทุกประเภท)' },
    ...courtTypes.map((ct) => ({ value: ct.id, label: ct.name })),
  ];

  const fmtAnnual = (c) => {
    const start = `${c.startDay} ${THAI_MONTHS[(c.startMonth || 1) - 1]}`;
    const isRange = c.endMonth !== c.startMonth || c.endDay !== c.startDay;
    return isRange ? `${start} – ${c.endDay} ${THAI_MONTHS[(c.endMonth || 1) - 1]}` : start;
  };
  const fmtOnce = (c) => (c.startDate === c.endDate ? c.startDate : `${c.startDate} → ${c.endDate}`);

  // Preview affected bookings only for one-off ranges.
  useEffect(() => {
    if (!showModal || formData.recurrence !== 'once' || !formData.startDate || !formData.endDate) { setAffected([]); return; }
    if (formData.startDate > formData.endDate) { setAffected([]); return; }
    let cancelled = false;
    setCheckingAffected(true);
    adminApiService.getAffectedBookings(token, { scope: formData.scope, startDate: formData.startDate, endDate: formData.endDate })
      .then((list) => { if (!cancelled) setAffected(list); })
      .catch(() => { if (!cancelled) setAffected([]); })
      .finally(() => { if (!cancelled) setCheckingAffected(false); });
    return () => { cancelled = true; };
  }, [showModal, formData, token]);

  const openCreate = () => { setEditing(null); setFormData(emptyForm); setAffected([]); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    if (c.recurrence === 'annual') {
      const multiDay = c.endMonth !== c.startMonth || c.endDay !== c.startDay;
      setFormData({
        ...emptyForm, recurrence: 'annual', scope: c.scope, reason: c.reason || '',
        startMonth: c.startMonth, startDay: c.startDay, endMonth: c.endMonth, endDay: c.endDay, multiDay,
      });
    } else {
      setFormData({ ...emptyForm, recurrence: 'once', scope: c.scope, reason: c.reason || '', startDate: c.startDate, endDate: c.endDate });
    }
    setShowModal(true);
  };

  const buildPayload = () => {
    const base = { scope: formData.scope, recurrence: formData.recurrence, reason: formData.reason };
    if (formData.recurrence === 'annual') {
      return {
        ...base,
        startMonth: formData.startMonth, startDay: formData.startDay,
        endMonth: formData.multiDay ? formData.endMonth : formData.startMonth,
        endDay: formData.multiDay ? formData.endDay : formData.startDay,
      };
    }
    return { ...base, startDate: formData.startDate, endDate: formData.endDate };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.recurrence === 'once') {
      if (!formData.startDate || !formData.endDate) { setError('กรุณาเลือกวันที่เริ่มและสิ้นสุด'); return; }
      if (formData.startDate > formData.endDate) { setError('วันที่เริ่มต้องอยู่ก่อนหรือตรงกับวันที่สิ้นสุด'); return; }
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      if (editing) await adminApiService.updateClosure(token, editing.id, payload);
      else await adminApiService.createClosure(token, payload);
      setShowModal(false);
      setFormData(emptyForm);
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm('ลบวันปิดทำการนี้?')) return;
    try { await adminApiService.deleteClosure(token, c.id); await fetchAll(); }
    catch (err) { setError(err.message); }
  };

  if (loading) return <div className="text-center py-10 text-gray-500">กำลังโหลด...</div>;

  const annual = closures.filter((c) => c.recurrence === 'annual');
  const oneOff = closures.filter((c) => c.recurrence !== 'annual');
  const today = todayStr();
  const upcoming = oneOff.filter((c) => (c.endDate || '') >= today).sort((a, b) => a.startDate.localeCompare(b.startDate));
  const past = oneOff.filter((c) => (c.endDate || '') < today).sort((a, b) => b.startDate.localeCompare(a.startDate));

  const row = (c, label) => (
    <li key={c.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900">{label}</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{scopeLabel(c.scope)}</span>
        </div>
        {c.reason && <p className="text-sm text-gray-500 mt-0.5">{c.reason}</p>}
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={() => openEdit(c)} className="px-2 py-1 text-sm text-blue-600 hover:text-blue-700">แก้ไข</button>
        <button onClick={() => handleDelete(c)} className="px-2 py-1 text-sm text-red-600 hover:text-red-700">ลบ</button>
      </div>
    </li>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">วันปิดทำการชั่วคราว</h2>
          <p className="text-sm text-gray-500">ตั้งวันหยุดประจำปี (เกิดซ้ำทุกปี) หรือวันปิดเฉพาะกิจ เพื่อปิดรับการจอง</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shrink-0">
          + เพิ่มวันปิดทำการ
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {closures.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg">ยังไม่มีวันปิดทำการ</div>
      ) : (
        <div className="space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">🔁 วันหยุดประจำปี (ทุกปี)</h3>
            {annual.length ? <ul className="space-y-2">{annual.map((c) => row(c, fmtAnnual(c)))}</ul>
              : <p className="text-sm text-gray-400">— ยังไม่มี —</p>}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">📅 วันที่เฉพาะกิจ (กำลังจะถึง)</h3>
            {upcoming.length ? <ul className="space-y-2">{upcoming.map((c) => row(c, fmtOnce(c)))}</ul>
              : <p className="text-sm text-gray-400">— ยังไม่มี —</p>}
            {past.length > 0 && (
              <div className="mt-3">
                <button onClick={() => setShowPast(!showPast)} className="text-xs text-gray-500 hover:text-gray-700">
                  {showPast ? '▾ ซ่อนวันที่ผ่านมาแล้ว' : `▸ แสดงวันที่ผ่านมาแล้ว (${past.length})`}
                </button>
                {showPast && <ul className="space-y-2 mt-2 opacity-60">{past.map((c) => row(c, fmtOnce(c)))}</ul>}
              </div>
            )}
          </section>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900">{editing ? 'แก้ไขวันปิดทำการ' : 'เพิ่มวันปิดทำการ'}</h3>

              {/* Mode toggle */}
              <div className="flex gap-2">
                {[{ v: 'annual', label: '🔁 ทุกปี' }, { v: 'once', label: '📅 เฉพาะวันที่' }].map((m) => (
                  <button key={m.v} type="button" onClick={() => setFormData({ ...formData, recurrence: m.v })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border ${formData.recurrence === m.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                    {m.label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ขอบเขต</label>
                <CustomDropdown value={formData.scope} onChange={(val) => setFormData({ ...formData, scope: val })} options={scopeOptions} placeholder="เลือกขอบเขต" />
              </div>

              {formData.recurrence === 'annual' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">เดือน</label>
                      <CustomDropdown value={formData.startMonth} onChange={(v) => setFormData({ ...formData, startMonth: v })} options={MONTH_OPTS} placeholder="เดือน" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">วันที่</label>
                      <CustomDropdown value={formData.startDay} onChange={(v) => setFormData({ ...formData, startDay: v })} options={DAY_OPTS} placeholder="วันที่" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={formData.multiDay} onChange={(e) => setFormData({ ...formData, multiDay: e.target.checked })} />
                    หลายวัน (เช่น สงกรานต์)
                  </label>
                  {formData.multiDay && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ถึงเดือน</label>
                        <CustomDropdown value={formData.endMonth} onChange={(v) => setFormData({ ...formData, endMonth: v })} options={MONTH_OPTS} placeholder="เดือน" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่</label>
                        <CustomDropdown value={formData.endDay} onChange={(v) => setFormData({ ...formData, endDay: v })} options={DAY_OPTS} placeholder="วันที่" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">วันที่เริ่ม</label>
                    <CustomDatePicker value={formData.startDate} onChange={(d) => setFormData({ ...formData, startDate: d, endDate: formData.endDate && formData.endDate < d ? d : formData.endDate })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สิ้นสุด</label>
                    <CustomDatePicker value={formData.endDate} min={formData.startDate || undefined} onChange={(d) => setFormData({ ...formData, endDate: d })} />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เหตุผล (ไม่บังคับ)</label>
                <input type="text" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="เช่น วันปีใหม่, ปิดปรับปรุงสนาม" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>

              {formData.recurrence === 'once' && (checkingAffected ? (
                <p className="text-xs text-gray-400">กำลังตรวจสอบการจองที่ได้รับผลกระทบ...</p>
              ) : affected.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">⚠️ มีการจองที่มีอยู่ {affected.length} รายการในช่วงนี้ — จะไม่ถูกยกเลิกอัตโนมัติ กรุณาจัดการเอง</p>
                  <ul className="text-xs text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                    {affected.map((b) => <li key={b.id}>• {b.date} {b.startTime}-{b.endTime} · {b.courtName} · {b.status}</li>)}
                  </ul>
                </div>
              ))}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">ยกเลิก</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'กำลังบันทึก...' : (editing ? 'บันทึก' : 'สร้าง')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosureManagement;
